import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react'
import PropTypes from 'prop-types'
import { useSelector } from 'react-redux'
import * as signalR from '@microsoft/signalr'
import { getUserName } from 'selectors/user'
import { getNormalizedApiHost } from 'utils/hosts'
import { getTabId } from 'utils/tabId'
import { getAccessToken } from 'auth/useAccessToken'
import { isInternalDomain } from 'auth/msalConfig'
import { generateDevJwt } from 'auth/generateDevJwt'
import { NotificationContext } from './NotificationContext'

const RECONNECT_INTERVALS = [0, 2000, 5000, 10000, 30000]

// When MSAL is active, use Bearer token for SignalR auth (same path as regular API calls).
// Otherwise fall back to a dev JWT (local dev) — x-username is no longer used for SignalR.
const isMsalEnabled = process.env.REACT_APP_ENABLE_MSAL === 'true'
const useMsalAuth = isMsalEnabled && isInternalDomain()

// Restrict dev-JWT generation to localhost only (matches the isLocalhost guard in request.js).
// On deployed environments where MSAL is disabled (e.g. plant-viewer, external/CIAM) the
// infrastructure provides its own auth (SiteMinder, Plant Viewer header) — sending an
// unsigned dev token there would bypass that boundary.
const isLocalhost =
  window.location.hostname === 'localhost' ||
  window.location.hostname === '127.0.0.1'
const useDevJwtAuth = !useMsalAuth && isLocalhost

/**
 * Build the SignalR hub connection options for the current environment.
 *
 * Exported so it can be unit-tested independently of the React component.
 * The three auth paths are:
 *   1. MSAL (deployed internal)  — accessTokenFactory → real AAD token
 *   2. Dev JWT (localhost only)  — accessTokenFactory → unsigned dev token
 *   3. Deployed non-MSAL         — no accessTokenFactory (infrastructure auth)
 *
 * @param {boolean} msalAuth   - Whether the MSAL path is active.
 * @param {boolean} devJwtAuth - Whether the dev-JWT path is active (localhost only).
 * @param {string}  username   - Redux username, used for dev-JWT generation.
 * @returns {object} Options object to pass to HubConnectionBuilder.withUrl().
 */
export function getSignalRAuthOptions(msalAuth, devJwtAuth, username) {
  if (msalAuth) {
    return { accessTokenFactory: () => getAccessToken() }
  }
  if (devJwtAuth) {
    return {
      accessTokenFactory: () =>
        Promise.resolve(generateDevJwt(username || 'DEFAULT')),
    }
  }
  // Deployed non-MSAL: infrastructure auth handles authentication — no factory.
  return {}
}

// Build hub URL based on environment (follows same pattern as request.js)
const getHubUrl = () => {
  const basePath = '/hub/notifications'
  const prefix = process.env.REACT_APP_API_PREFIX || ''

  if (process.env.NODE_ENV === 'production') {
    // In production, use absolute URL with API prefix (e.g., /v3)
    // getNormalizedApiHost() returns host without protocol, with fallbacks
    const host = getNormalizedApiHost()
    return `${window.location.protocol}//${host}${prefix}${basePath}`
  }

  // In development, route through local proxy at /hub (not /api/hub)
  return basePath
}

export function NotificationProvider({ children }) {
  const username = useSelector(getUserName)
  const connectionRef = useRef(null)
  const subscribedChannelsRef = useRef(new Set())

  const [connectionStatus, setConnectionStatus] = useState('connecting')
  const [notifications, setNotifications] = useState({})

  // Create connection
  const createConnection = useCallback(() => {
    const hubUrl = getHubUrl()
    const params = new URLSearchParams({ tabId: getTabId() })
    const url = `${hubUrl}?${params.toString()}`

    // Three auth paths for SignalR connections:
    // 1. MSAL (deployed internal): acquire real AAD token silently
    // 2. Dev JWT (localhost only): generate unsigned token from Redux username
    // 3. No accessTokenFactory (deployed non-MSAL): infrastructure auth handles it
    //    (SiteMinder, Plant Viewer hostname header, etc.)
    // The API's PromoteSignalRQueryParams handler converts the ?access_token query
    // param (WebSocket upgrade) to an Authorization header automatically for paths 1 & 2.
    const options = getSignalRAuthOptions(useMsalAuth, useDevJwtAuth, username)

    const connection = new signalR.HubConnectionBuilder()
      .withUrl(url, options)
      .withAutomaticReconnect(RECONNECT_INTERVALS)
      .configureLogging(signalR.LogLevel.Warning)
      .build()

    return connection
  }, [username])

  // Connect to hub
  useEffect(() => {
    if (!username) return undefined

    const connection = createConnection()
    connectionRef.current = connection

    connection.onreconnecting(() => {
      setConnectionStatus('reconnecting')
    })

    connection.onreconnected(() => {
      setConnectionStatus('connected')
    })

    connection.onclose((err) => {
      setConnectionStatus('disconnected')
      if (err) {
        console.error('[SignalR] Connection closed with error:', err)
      }
    })

    // Handle incoming notifications
    // Backend sends camelCase properties: channel, eventData, message, createdAt
    connection.on('ReceiveNotification', (notification) => {
      // Support both camelCase (backend) and PascalCase (legacy) property names
      const channel = notification.channel || notification.Channel
      const eventData = notification.eventData || notification.EventData
      if (channel) {
        setNotifications((prev) => ({
          ...prev,
          [channel.toLowerCase()]: eventData,
        }))
      }
    })

    connection
      .start()
      .then(() => {
        setConnectionStatus('connected')
      })
      .catch((err) => {
        console.error('[SignalR] Connection error:', err)
        setConnectionStatus('error')
      })

    return () => {
      connection.stop()
    }
  }, [username, createConnection])

  // Subscribe to a channel (client-side filtering only)
  // Backend broadcasts all notifications to user - no server subscription needed
  const subscribe = useCallback((channel) => {
    subscribedChannelsRef.current.add(channel.toLowerCase())
  }, [])

  // Unsubscribe from a channel (client-side filtering only)
  // Backend broadcasts all notifications to user - no server unsubscription needed
  const unsubscribe = useCallback((channel) => {
    subscribedChannelsRef.current.delete(channel.toLowerCase())
  }, [])

  // Clear a notification
  const clearNotification = useCallback((channel) => {
    setNotifications((prev) => {
      const next = { ...prev }
      delete next[channel.toLowerCase()]
      return next
    })
  }, [])

  // Performance fix: Memoize context value to prevent unnecessary re-renders
  // of all context consumers when provider re-renders
  const contextValue = useMemo(
    () => ({
      connectionStatus,
      notifications,
      subscribe,
      unsubscribe,
      clearNotification,
    }),
    [connectionStatus, notifications, subscribe, unsubscribe, clearNotification]
  )

  return (
    <NotificationContext.Provider value={contextValue}>
      {children}
    </NotificationContext.Provider>
  )
}

NotificationProvider.propTypes = {
  children: PropTypes.node.isRequired,
}
